import { Channel, EmailAuthMode, EmailOAuthProvider } from '../types';

/** Infer connection mode from channel record (matches server resolveOAuthProvider). */
export function resolveEmailAuthMode(channel: Channel | null | undefined): EmailAuthMode {
  if (!channel) return 'imap';
  if (channel.oauthProvider === 'google' || channel.oauthProvider === 'microsoft') {
    return channel.oauthProvider;
  }
  if (channel.accessToken || channel.oauthEmail) {
    const host = (channel.host || '').toLowerCase();
    if (host.includes('office365') || host.includes('outlook')) return 'microsoft';
    if (host.includes('gmail') || channel.oauthEmail) return 'google';
  }
  return 'imap';
}

export function isOAuthAuthMode(mode: EmailAuthMode): mode is Exclude<EmailOAuthProvider, null> {
  return mode === 'google' || mode === 'microsoft';
}

export function emailConnectionLabel(mode: EmailAuthMode): string {
  switch (mode) {
    case 'google': return 'Google';
    case 'microsoft': return 'M365';
    default: return 'IMAP';
  }
}

/** True when an OAuth email channel still needs the provider login flow. */
export function needsOAuthAuthorization(channel: Channel): boolean {
  const mode = resolveEmailAuthMode(channel);
  if (!isOAuthAuthMode(mode)) return false;
  // Tokens prove authorize completed even when oauthEmail was not yet resolved
  // (e.g. Google IMAP scope without openid/email on older grants).
  return !channel.oauthEmail && !channel.accessToken;
}

/** True when OAuth channel has completed provider authorization. */
export function isOAuthAuthorized(channel: Channel): boolean {
  const mode = resolveEmailAuthMode(channel);
  return isOAuthAuthMode(mode) && (!!channel.oauthEmail || !!channel.accessToken);
}

/**
 * Sidebar auth indicator for email channels.
 * - needs-auth: OAuth not completed, or Generic IMAP missing username
 * - authorized: OAuth has oauthEmail/accessToken, or Generic IMAP has username
 * - null: non-email channels (no mailbox auth)
 */
export type ChannelAuthStatus = 'authorized' | 'needs-auth';

export function channelAuthStatus(channel: Channel): ChannelAuthStatus | null {
  if (channel.type !== 'email') return null;
  const mode = resolveEmailAuthMode(channel);
  if (isOAuthAuthMode(mode)) {
    return isOAuthAuthorized(channel) ? 'authorized' : 'needs-auth';
  }
  return channel.username?.trim() ? 'authorized' : 'needs-auth';
}

/** Provider to use when starting authorize for this channel, or null if not OAuth. */
export function oauthProviderForAuthorize(channel: Channel): Exclude<EmailOAuthProvider, null> | null {
  const mode = resolveEmailAuthMode(channel);
  return isOAuthAuthMode(mode) ? mode : null;
}

/**
 * Server/client errors that mean the user must complete Google/Microsoft OAuth again.
 * Used when selecting a channel after tokens were revoked or never stored.
 */
export function isOAuthAuthorizationRequiredError(message: string | null | undefined): boolean {
  if (!message) return false;
  return /not authorized yet|re-authorize|oauth authentication failed|no oauth token|authorize with (google|microsoft)/i
    .test(message);
}

export function apiErrorMessage(err: unknown, fallback = 'Request failed'): string {
  const anyErr = err as { response?: { data?: { error?: string } }; message?: string };
  return anyErr?.response?.data?.error || anyErr?.message || fallback;
}

/** Where to return in Communications after OAuth callback. */
export type OAuthReturnIntent = 'channel' | 'settings';

export const OAUTH_RETURN_INTENT_KEY = 'communications-oauth-return-intent';

export function setOAuthReturnIntent(intent: OAuthReturnIntent): void {
  try {
    sessionStorage.setItem(OAUTH_RETURN_INTENT_KEY, intent);
  } catch {
    // sessionStorage may be unavailable
  }
}

export function takeOAuthReturnIntent(): OAuthReturnIntent {
  try {
    const value = sessionStorage.getItem(OAUTH_RETURN_INTENT_KEY);
    sessionStorage.removeItem(OAUTH_RETURN_INTENT_KEY);
    return value === 'channel' ? 'channel' : 'settings';
  } catch {
    return 'settings';
  }
}
